import { Badge, Box, Breadcrumb, Group, Heading, Tabs, TabsContent, TabsList, TabsTrigger } from '@optiaxiom/react';
import { IconCode } from '@optiaxiom/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, useApps, useAppVersions } from '@/lib/api';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';
import { targetProductIntent } from './targetProduct';
import { UNPUBLISHED_STATES } from './versionState';
import { VersionsTab } from './VersionsTab';
import { InstallationsTab } from './InstallationsTab';
import styles from './AppDetailPage.module.css';

export function AppDetailPage() {
  const { appId = '' } = useParams();
  const navigate = useNavigate();
  // The app name/target come from the (cached) apps list; on a direct load this fetches it.
  const { data } = useApps();
  const app = data?.items.find((item) => item.appId === appId);
  const name = app?.name ?? appId;

  // The versions endpoint enforces app existence + ownership, so a 404 there is the
  // authoritative "no such app" signal (reachable now that app ids can be deep-linked).
  // UNPUBLISHED_STATES matches VersionsTab's initial filter exactly, which is what keeps the
  // two on one query key and so one request — pass anything else and the page fans out twice.
  // Only the 404 is read here, deliberately not the toggle: threading that through would
  // re-render the whole page on every flip and fetch a second variant for no gain.
  const { error: versionsError } = useAppVersions(appId, UNPUBLISHED_STATES);
  const appNotFound = versionsError instanceof ApiError && versionsError.status === 404;

  if (appNotFound) {
    return (
      <NotFoundPage
        title="App not found"
        description={
          <>
            We couldn&rsquo;t find an app with ID <strong>{appId}</strong>. It may not exist, or you
            may not have access to it.
          </>
        }
      />
    );
  }

  return (
    <Group flexDirection="column" gap="16" alignItems="stretch" className={styles.page}>
      <Breadcrumb
        items={[
          // execute (not href) so it navigates client-side via React Router rather than a
          // full page load — important inside the Prism iframe.
          { label: 'Apps', execute: () => navigate('/apps') },
          { label: name },
        ]}
      />

      <Group flexDirection="row" gap="16" alignItems="center">
        <Box className={styles.appIcon}>
          <IconCode size={28} />
        </Box>
        <Group flexDirection="row" gap="12" alignItems="center">
          <Heading level="1">{name}</Heading>
          {app && (
            <Badge intent={targetProductIntent(app.targetProduct)} variant="subtle">
              {app.targetProduct}
            </Badge>
          )}
        </Group>
      </Group>

      <Tabs defaultValue="versions" className={styles.tabs}>
        <TabsList>
          <TabsTrigger value="versions">Versions</TabsTrigger>
          <TabsTrigger value="installations">Installations</TabsTrigger>
        </TabsList>
        <TabsContent value="versions" className={styles.tabsContent}>
          <Box mt="16" className={styles.tabScroll}>
            <VersionsTab appId={appId} />
          </Box>
        </TabsContent>
        <TabsContent value="installations" className={styles.tabsContent}>
          <Box mt="16" className={styles.tabScroll}>
            <InstallationsTab appId={appId} />
          </Box>
        </TabsContent>
      </Tabs>
    </Group>
  );
}
